import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog"
import { Button } from "../../../components/ui/button"
import { Badge } from "../../../components/ui/badge"
import { Card, CardContent } from "../../../components/ui/card"
import { Separator } from "../../../components/ui/separator"
import { Copy, Check, ExternalLink, MoreVertical, Package, Building2 } from "lucide-react"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../components/ui/tooltip"
import { useNavigate, useParams } from "react-router-dom";
import { useUserContext } from "../../../Contexts/UserProvider";
import { Sheet, SheetContent, SheetHeader, SheetFooter, SheetTitle, SheetTrigger } from "../../../components/ui/sheet"

const PartnerDetailsView = ({ record, open, onOpenChange }) => {
  const [copied, setCopied] = useState(false);
  const { id } = useParams();
  const { tenant } = useUserContext();
  const navigate = useNavigate();

  useEffect(() => {
    console.log("Data : ", record, open);
  }, [open])

  // if (!record) return null

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  // Get status badge color
  const getStatusColor = (status: string) => {
    switch (status) {
      case "Accept":
        return "bg-green-100 text-green-800"
      case "Pending":
        return "bg-yellow-100 text-yellow-800"
      case "Canceled":
        return "bg-red-100 text-red-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  const handleNavigateToCatalogue = () => {
    navigate(`/app/${id}/products/${record?.invitedToTenantId}?name=${encodeURIComponent(record?.invitedToTenantName)}`);
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full min-w-[600px] p-0 overflow-auto">
        <SheetHeader className="bg-gradient-to-r from-purple-50 to-blue-50 p-6 border-b detail-header">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-purple-100 p-2 rounded-lg">
                <Building2 className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <SheetTitle className="text-xl font-bold text-gray-900">Company Details</SheetTitle>
                <p className="text-sm text-gray-600 mt-1">Created on {formatDate(record?.createdDate)}</p>
                <span><Badge className={`ml-auto ${getStatusColor(record?.notificationStatus)}`}>
                  {record?.notificationStatus}
                </Badge></span>
              </div>
            </div>
          </div>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="close-panel">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-x h-4 w-4"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>
          </Button>
        </SheetHeader>

        <div className="p-6">
          <TooltipProvider>
            <div className="space-y-6">
              <div>
                <p className="text-sm text-gray-500 mb-1">Invitation ID</p>
                <div className="flex items-center">
                  <p className="text-sm font-mono truncate mr-2 bg-gray-800 text-gray-100 border border-gray-700 p-2 rounded flex-1">{record?.id}</p>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="outline" size="sm" className="h-8" onClick={() => copyToClipboard(record?.id)}>
                        {copied ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
                        Copy
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Copy ID</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-gray-500 mb-1">User Email</p>
                  <div className="flex items-center">
                    <p className="font-medium">{record?.userEmail}</p>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 ml-1"
                      onClick={() => copyToClipboard(record?.userEmail)}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Partnership Type</p>
                  <p className="font-medium">{record?.userType}</p>
                </div>
              </div>

              <Separator />

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Invited From</p>
                  <p className="font-medium">{record?.invitedFromTenantName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Invited To</p>
                  <p className="font-medium">{record?.invitedTo || "—"}</p>
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-500 mb-1">Invited By</p>
                <p className="font-medium">{record?.invitedFromUserName}</p>
              </div>

              {/* <Card className="bg-gray-50 border">
                <CardContent className="p-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">From Tenant ID</p>
                      <div className="flex items-center">
                        <p className="text-sm font-mono truncate text-gray-100">{record?.invitedFromTenantId}</p>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 ml-1"
                          onClick={() => copyToClipboard(record?.invitedFromTenantId)}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">To Tenant ID</p>
                      <div className="flex items-center">
                        <p className="text-sm font-mono truncate text-gray-100">{record?.invitedToTenantId || "—"}</p>
                        {record?.invitedToTenantId && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 ml-1"
                            onClick={() => copyToClipboard(record?.invitedToTenantId)}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card> */}

              {/* <div className="border-t p-6 flex justify-end gap-2">
                {record?.invitedToTenantId !== tenant?.id && (
                  <Button variant="outline" className="flex items-center bg-primary" onClick={handleNavigateToCatalogue}>
                    <ExternalLink className="h-4 w-4 mr-2" />
                    View Company Catalogue
                  </Button>
                )}
              </div> */}
            </div>
          </TooltipProvider>
        </div>
        <SheetFooter>
          <div className="absolute bottom-0 left-0 right-0 px-6 py-4 border-t border-border bg-card flex justify-end gap-3">
            <Button variant="outline" className="flex items-center bg-primary" onClick={handleNavigateToCatalogue}>
              <ExternalLink className="h-4 w-4 mr-2" />
              View Company Catalogue
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
};

export default PartnerDetailsView;
